import { supabase } from '../lib/supabase';

/**
 * Helper: Normalize phone number to local French format (0XXXXXXXXX)
 * Handles: 0652898986, +33652898986, 06 52 89 89 86, 0033652898986
 */
const normalizePhone = (phone) => {
  if (!phone) return null;
  let cleaned = phone?.trim()?.replace(/[\s\-\.\(\)]/g, '');
  // 0033XXXXXXXXX → +33XXXXXXXXX
  if (cleaned?.startsWith('0033')) {
    cleaned = '+33' + cleaned?.slice(4);
  }
  // +33XXXXXXXXX → 0XXXXXXXXX
  if (cleaned?.startsWith('+33')) {
    cleaned = '0' + cleaned?.slice(3);
  }
  // 9-digit without leading zero
  if (cleaned?.length === 9 && /^[1-9]/?.test(cleaned)) {
    cleaned = '0' + cleaned;
  }
  return cleaned;
};

/**
 * Helper: Find or create a client from barrel/carton order data
 * Uses the Supabase RPC function find_or_create_material_client
 */
const findOrCreateMaterialClient = async (orderData, orderType = 'barrel') => {
  try {
    const { data: clientId, error } = await supabase?.rpc('find_or_create_material_client', {
      p_name: orderData?.customerName || orderData?.client_name || 'Client inconnu',
      p_phone: orderData?.customerPhone || orderData?.client_phone || null,
      p_email: orderData?.customerEmail || orderData?.client_email || null,
      p_address: orderData?.customerAddress || orderData?.client_address || null,
      p_city: orderData?.customerCity || orderData?.client_city || null,
      p_postal_code: orderData?.customerPostalCode || orderData?.postal_code || null,
      p_source: 'barriques_cartons',
      p_order_type: orderType
    });

    if (error) {
      console.error('Error finding/creating material client:', error);
      return null;
    }
    return clientId;
  } catch (err) {
    console.error('findOrCreateMaterialClient error:', err);
    return null;
  }
};

/**
 * Service for managing barrel orders and deposits
 */

export const barrelService = {
  /**
   * Fetch all barrel orders with optional filters
   */
  async getBarrelOrders(filters = {}) {
    try {
      let query = supabase
        ?.from('barrel_sales')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (filters?.paymentStatus) {
        query = query?.eq('payment_status', filters?.paymentStatus);
      }

      if (filters?.orderStatus) {
        query = query?.eq('order_status', filters?.orderStatus);
      }

      if (filters?.clientId) {
        query = query?.eq('client_id', filters?.clientId);
      }

      if (filters?.startDate) {
        query = query?.gte('order_date', filters?.startDate);
      }

      if (filters?.endDate) {
        query = query?.lte('order_date', filters?.endDate);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Map snake_case to camelCase for frontend consistency
      return data?.map(sale => ({
        id: sale?.id,
        clientId: sale?.client_id,
        clientName: sale?.customer_name,
        clientPhone: sale?.customer_phone,
        barrelType: sale?.barrel_type,
        quantity: sale?.quantity,
        unitPrice: sale?.unit_price,
        totalPrice: sale?.total_price,
        paymentStatus: sale?.payment_status,
        orderStatus: sale?.order_status,
        orderDate: sale?.order_date,
        notes: sale?.notes,
        createdAt: sale?.created_at,
        updatedAt: sale?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching barrel orders:', error);
      throw error;
    }
  },

  /**
   * Fetch single barrel order by ID
   */
  async getBarrelOrderById(id) {
    try {
      const { data, error } = await supabase
        ?.from('barrel_sales')
        ?.select('*')
        ?.eq('id', id)
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching barrel order:', error);
      throw error;
    }
  },

  /**
   * Create new barrel order
   */
  async createBarrelOrder(orderData) {
    try {
      // Auto-create or link client
      let clientId = orderData?.client_id || null;
      if (!clientId && (orderData?.customer_phone || orderData?.customer_name)) {
        clientId = await findOrCreateMaterialClient({
          customerName: orderData?.customer_name,
          customerPhone: orderData?.customer_phone,
          customerEmail: orderData?.customer_email,
          customerAddress: orderData?.customer_address,
          customerCity: orderData?.customer_city,
          customerPostalCode: orderData?.postal_code
        }, 'barrel');
      }

      const { data, error } = await supabase
        ?.from('barrel_sales')
        ?.insert({ ...orderData, client_id: clientId })
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating barrel order:', error);
      throw error;
    }
  },

  /**
   * Update barrel order
   */
  async updateBarrelOrder(id, updates) {
    try {
      const { data, error } = await supabase
        ?.from('barrel_sales')
        ?.update(updates)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating barrel order:', error);
      throw error;
    }
  },

  /**
   * Delete barrel order
   */
  async deleteBarrelOrder(id) {
    try {
      const { error } = await supabase
        ?.from('barrel_sales')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting barrel order:', error);
      throw error;
    }
  },

  /**
   * Fetch all barrel deposits with optional filters
   */
  async getBarrelDeposits(filters = {}) {
    try {
      let query = supabase
        ?.from('barrel_deposits')
        ?.select('*, employees(*), barrel_sales!sale_id(*), clients(*)')
        ?.order('planned_collection_date', { ascending: true });

      if (filters?.collectionStatus) {
        query = query?.eq('collection_status', filters?.collectionStatus);
      }

      if (filters?.driverId) {
        query = query?.eq('assigned_driver_id', filters?.driverId);
      }

      if (filters?.startDate) {
        query = query?.gte('planned_collection_date', filters?.startDate);
      }

      if (filters?.endDate) {
        query = query?.lte('planned_collection_date', filters?.endDate);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching barrel deposits:', error);
      throw error;
    }
  },

  /**
   * Fetch single barrel deposit by ID
   */
  async getBarrelDepositById(id) {
    try {
      const { data, error } = await supabase
        ?.from('barrel_deposits')
        ?.select('*, employees(*), barrel_sales!sale_id(*)')
        ?.eq('id', id)
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching barrel deposit:', error);
      throw error;
    }
  },

  /**
   * Create new barrel deposit
   */
  async createBarrelDeposit(depositData) {
    try {
      const { data, error } = await supabase
        ?.from('barrel_deposits')
        ?.insert(depositData)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating barrel deposit:', error);
      throw error;
    }
  },

  /**
   * Update barrel deposit
   */
  async updateBarrelDeposit(id, updates) {
    try {
      const { data, error } = await supabase
        ?.from('barrel_deposits')
        ?.update(updates)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating barrel deposit:', error);
      throw error;
    }
  },

  /**
   * Delete barrel deposit
   */
  async deleteBarrelDeposit(id) {
    try {
      const { error } = await supabase
        ?.from('barrel_deposits')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting barrel deposit:', error);
      throw error;
    }
  },

  /**
   * Reactivate a cancelled barrel deposit
   */
  async reactivateBarrelDeposit(id) {
    try {
      const { data, error } = await supabase
        ?.rpc('reactivate_barrel_deposit', { deposit_id: id });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error reactivating barrel deposit:', error);
      throw error;
    }
  },

  /**
   * Get barrel statistics
   */
  async getBarrelStatistics() {
    try {
      const [ordersResult, depositsResult] = await Promise.all([
        supabase?.from('barrel_sales')?.select('order_status, quantity, total_price'),
        supabase?.from('barrel_deposits')?.select('collection_status, quantity')
      ]);

      const orders = ordersResult?.data || [];
      const deposits = depositsResult?.data || [];

      return {
        totalOrders: orders?.length,
        totalRevenue: orders?.reduce((sum, order) => sum + parseFloat(order?.total_price || 0), 0),
        pendingOrders: orders?.filter(o => o?.order_status === 'pending')?.length,
        deliveredOrders: orders?.filter(o => o?.order_status === 'delivered')?.length,
        totalDeposits: deposits?.length,
        pendingCollections: deposits?.filter(d => d?.collection_status === 'pending')?.length,
        scheduledCollections: deposits?.filter(d => d?.collection_status === 'scheduled')?.length,
        collectedDeposits: deposits?.filter(d => d?.collection_status === 'collected')?.length
      };
    } catch (error) {
      console.error('Error fetching barrel statistics:', error);
      throw error;
    }
  },

  /**
   * Create new barrel sale
   */
  async createSale(saleData) {
    try {
      // Auto-create or link client
      let clientId = saleData?.clientId || null;
      if (!clientId && (saleData?.customerPhone || saleData?.customerName)) {
        clientId = await findOrCreateMaterialClient(saleData, 'barrel');
      }

      // Map camelCase to snake_case for database
      const dbData = {
        customer_name: saleData?.customerName,
        customer_phone: saleData?.customerPhone,
        barrel_type: saleData?.barrelType,
        quantity: saleData?.quantity,
        unit_price: saleData?.unitPrice,
        total_price: saleData?.totalPrice,
        purchase_price: saleData?.purchasePrice || 0,
        payment_status: saleData?.paymentStatus,
        order_status: saleData?.orderStatus,
        notes: saleData?.notes,
        client_id: clientId
      };

      const { data, error } = await supabase
        ?.from('barrel_sales')
        ?.insert(dbData)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating sale:', error);
      throw error;
    }
  },

  /**
   * Update barrel sale
   */
  async updateSale(id, updates) {
    try {
      // Map camelCase to snake_case for database
      const dbUpdates = {};
      if (updates?.customerName !== undefined) dbUpdates.customer_name = updates?.customerName;
      if (updates?.customerPhone !== undefined) dbUpdates.customer_phone = updates?.customerPhone;
      if (updates?.barrelType !== undefined) dbUpdates.barrel_type = updates?.barrelType;
      if (updates?.quantity !== undefined) dbUpdates.quantity = updates?.quantity;
      if (updates?.unitPrice !== undefined) dbUpdates.unit_price = updates?.unitPrice;
      if (updates?.totalPrice !== undefined) dbUpdates.total_price = updates?.totalPrice;
      if (updates?.paymentStatus !== undefined) dbUpdates.payment_status = updates?.paymentStatus;
      if (updates?.orderStatus !== undefined) dbUpdates.order_status = updates?.orderStatus;
      if (updates?.notes !== undefined) dbUpdates.notes = updates?.notes;

      const { data, error } = await supabase
        ?.from('barrel_sales')
        ?.update(dbUpdates)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating barrel sale:', error);
      throw error;
    }
  },

  /**
   * Helper method: Get barrel inventory
   */
  async getInventory() {
    try {
      const { data, error } = await supabase
        ?.from('barrel_inventory')
        ?.select('*');

      if (error) throw error;
      
      // Map snake_case to camelCase
      return data?.map(item => ({
        id: item?.id,
        barrelType: item?.barrel_type,
        priceEuros: item?.price_euros,
        stockQuantity: item?.stock_quantity,
        reorderThreshold: item?.reorder_threshold,
        supplierInfo: item?.supplier_info,
        lastRestockedAt: item?.last_restocked_at,
        createdAt: item?.created_at,
        updatedAt: item?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching barrel inventory:', error);
      throw error;
    }
  },

  /**
   * Adjust barrel stock (positive = add, negative = deduct)
   */
  async adjustBarrelStock(barrelType, adjustment) {
    try {
      const { data: current, error: fetchError } = await supabase
        ?.from('barrel_inventory')
        ?.select('id, stock_quantity')
        ?.eq('barrel_type', barrelType)
        ?.single();
      if (fetchError) throw fetchError;
      const newQty = Math.max(0, (current?.stock_quantity || 0) + adjustment);
      const { data, error } = await supabase
        ?.from('barrel_inventory')
        ?.update({ stock_quantity: newQty, updated_at: new Date()?.toISOString() })
        ?.eq('barrel_type', barrelType)
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adjusting barrel stock:', error);
      throw error;
    }
  },

  /**
   * Helper method: Get deposits (alias for getBarrelDeposits)
   */
  async getDeposits(filters = {}) {
    try {
      let query = supabase
        ?.from('barrel_deposits')
        ?.select('*')
        ?.order('planned_collection_date', { ascending: true });

      if (filters?.depositStatus) {
        query = query?.eq('collection_status', filters?.depositStatus);
      }

      if (filters?.driverId) {
        query = query?.eq('assigned_driver_id', filters?.driverId);
      }

      if (filters?.startDate) {
        query = query?.gte('planned_collection_date', filters?.startDate);
      }

      if (filters?.endDate) {
        query = query?.lte('planned_collection_date', filters?.endDate);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Map snake_case to camelCase
      return data?.map(deposit => ({
        id: deposit?.id,
        clientId: deposit?.client_id,
        clientName: deposit?.client_name,
        clientPhone: deposit?.client_phone,
        clientAddress: deposit?.client_address,
        clientCity: deposit?.client_city,
        barrelType: deposit?.barrel_type,
        quantity: deposit?.quantity,
        totalPrice: deposit?.total_price,
        paymentStatus: deposit?.payment_status,
        collectionStatus: deposit?.collection_status,
        plannedCollectionDate: deposit?.planned_collection_date,
        deliveryDate: deposit?.delivery_date,
        deliveryNotes: deposit?.delivery_notes,
        assignedDriverId: deposit?.assigned_driver_id,
        routeOrder: deposit?.route_order,
        deliveryPhotoUrl: deposit?.delivery_photo_url,
        signatureUrl: deposit?.signature_url,
        timeSlot: deposit?.time_slot,
        createdAt: deposit?.created_at,
        updatedAt: deposit?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching deposits:', error);
      throw error;
    }
  },

  /**
   * Helper method: Create deposit with proper field mapping
   */
  async createDeposit(depositData) {
    try {
      // Map camelCase to snake_case for database
      const dbData = {
        client_id: depositData?.clientId,
        client_name: depositData?.clientName,
        client_phone: depositData?.clientPhone,
        client_address: depositData?.clientAddress,
        client_city: depositData?.clientCity,
        barrel_type: depositData?.barrelType,
        quantity: depositData?.quantity,
        total_price: depositData?.totalPrice,
        payment_status: depositData?.paymentStatus,
        collection_status: depositData?.collectionStatus,
        planned_collection_date: depositData?.plannedCollectionDate,
        delivery_date: depositData?.deliveryDate,
        delivery_notes: depositData?.deliveryNotes,
        assigned_driver_id: depositData?.assignedDriverId,
        route_order: depositData?.routeOrder,
        time_slot: depositData?.timeSlot
      };

      const { data, error } = await supabase
        ?.from('barrel_deposits')
        ?.insert(dbData)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating deposit:', error);
      throw error;
    }
  },

  /**
   * Helper method: Update deposit with proper field mapping
   */
  async updateDeposit(id, updates) {
    try {
      // Map camelCase to snake_case for database
      const dbUpdates = {};
      if (updates?.clientId !== undefined) dbUpdates.client_id = updates?.clientId;
      if (updates?.clientName !== undefined) dbUpdates.client_name = updates?.clientName;
      if (updates?.clientPhone !== undefined) dbUpdates.client_phone = updates?.clientPhone;
      if (updates?.clientAddress !== undefined) dbUpdates.client_address = updates?.clientAddress;
      if (updates?.clientCity !== undefined) dbUpdates.client_city = updates?.clientCity;
      if (updates?.barrelType !== undefined) dbUpdates.barrel_type = updates?.barrelType;
      if (updates?.quantity !== undefined) dbUpdates.quantity = updates?.quantity;
      if (updates?.totalPrice !== undefined) dbUpdates.total_price = updates?.totalPrice;
      if (updates?.paymentStatus !== undefined) dbUpdates.payment_status = updates?.paymentStatus;
      if (updates?.collectionStatus !== undefined) dbUpdates.collection_status = updates?.collectionStatus;
      if (updates?.plannedCollectionDate !== undefined) dbUpdates.planned_collection_date = updates?.plannedCollectionDate;
      if (updates?.deliveryDate !== undefined) dbUpdates.delivery_date = updates?.deliveryDate;
      if (updates?.deliveryNotes !== undefined) dbUpdates.delivery_notes = updates?.deliveryNotes;
      if (updates?.assignedDriverId !== undefined) dbUpdates.assigned_driver_id = updates?.assignedDriverId;
      if (updates?.routeOrder !== undefined) dbUpdates.route_order = updates?.routeOrder;
      if (updates?.timeSlot !== undefined) dbUpdates.time_slot = updates?.timeSlot;
      
      // Support direct snake_case updates (for backward compatibility with existing code)
      if (updates?.assigned_driver_id !== undefined) dbUpdates.assigned_driver_id = updates?.assigned_driver_id;
      if (updates?.collection_status !== undefined) dbUpdates.collection_status = updates?.collection_status;

      const { data, error } = await supabase
        ?.from('barrel_deposits')
        ?.update(dbUpdates)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating deposit:', error);
      throw error;
    }
  },

  /**
   * Helper method: Delete deposit (alias for deleteBarrelDeposit)
   */
  async deleteDeposit(id) {
    return this.deleteBarrelDeposit(id);
  }
};

export default barrelService;